import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    ScanFace, ShieldCheck, ShieldX, RotateCcw, Loader2, AlertTriangle,
    ExternalLink, CheckCircle2, ImageOff, Maximize2, CalendarX
} from 'lucide-react';
import { supabase } from '../../../../src/lib/supabase';
import { logUsageEvent } from '../../../../src/lib/logger';
import { useAuth } from '../../../../src/contexts/AuthContext';
import { useToast } from '../../../../src/contexts/ToastContext';

/**
 * Decisão da verificação biométrica MANUAL do termo, dentro da análise cadastral.
 *
 * Quando a biometria facial do cliente falha nas 3 tentativas automáticas (DV040,
 * "face não encontrada nas bases"), o Autentique não reprova: manda para conferência
 * manual, bloqueia o documento e avisa apenas o AUTOR do documento — a credencial do
 * n8n. Quem precisa da informação (o analista) nunca fica sabendo, e a análise trava
 * sem PDF assinado e sem selfie para conferir. Foi o que segurou as negociações 3460
 * (14 dias) e 4194 (21 dias).
 *
 * Este painel mostra a selfie ao lado dos dados informados e resolve a fila aqui:
 * aprovar, reprovar ou devolver tentativas ao cliente. A regra de quem pode decidir
 * é a da edge function `biometria-termo` — o gate abaixo é só UX.
 *
 * O wizard roda em fundo CLARO (o card escuro da ficha é a exceção, não a regra),
 * então toda cor aqui tem par claro/escuro. A primeira versão usava só tons de
 * `slate-200/400` e ficou ilegível no tema claro.
 */

/** Papéis que decidem biometria. Espelha PAPEIS_AUTORIZADOS da edge function. */
const PAPEIS_DECIDEM = new Set(['adm', 'admin', 'gerente', 'analista']);

type Acao = 'aprovar' | 'reprovar' | 'liberar_tentativas';

interface Tentativa {
    numero: number | null;
    codigo: string | null;
    mensagem: string | null;
    tipo: string | null;
    imagem: string | null;
    dados: { cpf?: string; name?: string; birthday?: string } | null;
}

interface StatusBiometria {
    numero_termo: string | null;
    url_documento: string;
    /** Prazo de assinatura do documento no Autentique (10 dias no fluxo do n8n). */
    prazo_assinatura: string | null;
    /** Prazo vencido com a biometria ainda pendente: o Autentique cancelou o
     * documento e nenhuma decisão é mais aceita — só reemitir o termo. */
    documento_expirado: boolean;
    assinatura: {
        nome: string | null;
        email: string | null;
        assinado_em: string | null;
        aguardando_verificacao: boolean;
        biometria_aprovada_em: string | null;
        biometria_reprovada_em: string | null;
        verificacao: { id: number | null; tipo: string | null; pendente: boolean; verificada_em: string | null } | null;
        selfie: string | null;
        dados_informados: { cpf?: string; name?: string; birthday?: string } | null;
        tentativas: Tentativa[];
    };
}

interface BiometriaPanelProps {
    negociacaoId: number | string;
    /** Aponta um termo específico; sem isso a função decide pelo termo operante. */
    numeroTermo?: string;
}

const fmtDataHora = (iso?: string | null) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return isNaN(d.getTime()) ? '—' : d.toLocaleString('pt-BR', {
        day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
};

const fmtNascimento = (iso?: string) => {
    if (!iso) return '—';
    const [ano, mes, dia] = iso.split('-');
    return dia && mes && ano ? `${dia}/${mes}/${ano}` : iso;
};

/** O CPF vem cru do Autentique (11 dígitos) — o analista compara com o documento,
 * então tem que sair pontuado como no RG/CNH. */
const fmtCpf = (cpf?: string) => {
    const d = (cpf || '').replace(/\D/g, '');
    return d.length === 11 ? `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}` : (cpf || '—');
};

/** `functions.invoke` devolve só "non-2xx status code" e engole a causa; o motivo
 * real vem em `error.context`. Mesmo padrão de src/lib/gatewayResolver.ts. */
const chamar = async (body: Record<string, unknown>): Promise<StatusBiometria> => {
    const { data, error } = await supabase.functions.invoke('biometria-termo', { body });
    if (error) {
        const detalhe = await (error as any)?.context?.json?.().catch(() => null);
        throw new Error(detalhe?.error || error.message || 'Falha ao falar com o Autentique.');
    }
    if (data?.success === false) throw new Error(data.error || 'Falha ao falar com o Autentique.');
    return data as StatusBiometria;
};

/** Rótulo de um campo comparável com o documento do cliente. */
const Campo = ({ label, valor, mono }: { label: string; valor: React.ReactNode; mono?: boolean }) => (
    <div className="min-w-0">
        <span className="block text-[9px] font-bold uppercase tracking-[0.14em] text-gray-500 dark:text-slate-400">
            {label}
        </span>
        <span className={`block text-sm font-bold text-gray-900 dark:text-slate-100 truncate ${mono ? 'font-mono' : ''}`}>
            {valor || '—'}
        </span>
    </div>
);

export default function BiometriaPanel({ negociacaoId, numeroTermo }: BiometriaPanelProps) {
    const { profile } = useAuth();
    const { addToast } = useToast();

    const [status, setStatus] = useState<StatusBiometria | null>(null);
    const [carregando, setCarregando] = useState(true);
    const [erro, setErro] = useState<string | null>(null);
    const [executando, setExecutando] = useState<Acao | null>(null);
    const [confirmando, setConfirmando] = useState<Acao | null>(null);
    const [motivo, setMotivo] = useState('');
    const [ampliada, setAmpliada] = useState(false);

    const podeDecidir = PAPEIS_DECIDEM.has(String(profile?.role || ''));

    const carregar = useCallback(async () => {
        setCarregando(true);
        setErro(null);
        try {
            setStatus(await chamar({ negociacao_id: Number(negociacaoId), acao: 'status', numero_termo: numeroTermo }));
        } catch (e: any) {
            setErro(e?.message || 'Não foi possível consultar a verificação.');
        } finally {
            setCarregando(false);
        }
    }, [negociacaoId, numeroTermo]);

    useEffect(() => { void carregar(); }, [carregar]);

    /** As 3 tentativas automáticas costumam falhar pelo MESMO motivo — listar a
     * mesma frase três vezes só ocupa espaço. Agrupa e conta. */
    const falhas = useMemo(() => {
        const porMensagem = new Map<string, { codigo: string | null; mensagem: string; vezes: number }>();
        (status?.assinatura.tentativas || []).forEach(t => {
            if (!t.codigo) return;
            const chave = `${t.codigo}|${t.mensagem || ''}`;
            const atual = porMensagem.get(chave);
            if (atual) atual.vezes += 1;
            else porMensagem.set(chave, { codigo: t.codigo, mensagem: t.mensagem || 'Falha não detalhada', vezes: 1 });
        });
        return [...porMensagem.values()];
    }, [status]);

    const executar = async (acao: Acao) => {
        setExecutando(acao);
        try {
            const novo = await chamar({
                negociacao_id: Number(negociacaoId),
                acao,
                numero_termo: numeroTermo,
                ...(acao === 'reprovar' ? { motivo: motivo.trim() } : {}),
            });
            setStatus(novo);
            setConfirmando(null);
            setMotivo('');
            addToast(
                acao === 'aprovar'
                    ? 'Biometria aprovada. O Autentique vai liberar o PDF assinado em instantes.'
                    : acao === 'reprovar'
                        ? 'Biometria reprovada. O cliente precisará assinar o termo novamente.'
                        : 'Novas tentativas liberadas. Avise o cliente para refazer a selfie pelo link de assinatura.',
                'success'
            );
            logUsageEvent({ userId: profile?.id, modulo: 'gestao', funcionalidade: 'dashboard', acao: 'decidir_biometria_termo', origem: 'acao' });
        } catch (e: any) {
            addToast(e?.message || 'Não foi possível concluir a operação.', 'error');
        } finally {
            setExecutando(null);
        }
    };

    if (carregando) {
        return (
            <div className="flex items-center gap-3 px-5 py-4 rounded-2xl border border-amber-300/70 bg-amber-50 text-amber-800 dark:border-amber-500/25 dark:bg-amber-500/10 dark:text-amber-200 text-sm font-medium">
                <Loader2 size={16} className="animate-spin" />
                Consultando a verificação biométrica no Autentique…
            </div>
        );
    }

    if (erro) {
        return (
            <div className="flex items-start gap-3 px-5 py-4 rounded-2xl border border-red-300 bg-red-50 text-red-800 dark:border-red-500/25 dark:bg-red-500/10 dark:text-red-200 text-sm">
                <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                    <p className="font-bold">Não foi possível consultar a biometria</p>
                    <p className="text-red-700/80 dark:text-red-300/80 text-xs mt-0.5">{erro}</p>
                </div>
                <button
                    onClick={() => void carregar()}
                    className="text-xs font-bold underline hover:no-underline flex-shrink-0"
                >
                    Tentar de novo
                </button>
            </div>
        );
    }

    const assinatura = status?.assinatura;
    if (!assinatura?.verificacao) return null;

    const expirado = !!status?.documento_expirado;
    const pendente = assinatura.verificacao.pendente && !expirado;
    const reprovada = !!assinatura.biometria_reprovada_em;
    const dados = assinatura.dados_informados;

    // Dias que ainda restam para decidir. O relógio do prazo de assinatura não para
    // enquanto a biometria espera conferência — quando ele zera, a assinatura do
    // cliente é perdida junto com o documento.
    const diasRestantes = status?.prazo_assinatura
        ? Math.ceil((Date.parse(status.prazo_assinatura) - Date.now()) / 86_400_000)
        : null;

    return (
        <>
            <div className={`rounded-2xl border overflow-hidden shadow-sm ${expirado
                ? 'border-red-300 bg-red-50/60 dark:border-red-500/30 dark:bg-red-500/[0.06]'
                : 'border-amber-300/80 bg-amber-50/60 dark:border-amber-500/30 dark:bg-amber-500/[0.06]'}`}
            >
                {/* Cabeçalho */}
                <div className={`flex items-start gap-3 px-5 py-4 border-b ${expirado
                    ? 'border-red-300/60 bg-red-100/50 dark:border-red-500/20 dark:bg-transparent'
                    : 'border-amber-300/60 bg-amber-100/50 dark:border-amber-500/20 dark:bg-transparent'}`}
                >
                    <div className={`p-2 rounded-xl border flex-shrink-0 ${expirado
                        ? 'bg-red-500/15 text-red-700 border-red-500/30 dark:text-red-300'
                        : 'bg-amber-500/15 text-amber-700 border-amber-500/30 dark:text-amber-300'}`}
                    >
                        {expirado ? <CalendarX size={18} /> : <ScanFace size={18} />}
                    </div>
                    <div className="flex-1 min-w-0">
                        <h4 className={`text-sm font-black font-poppins ${expirado
                            ? 'text-red-900 dark:text-red-200'
                            : 'text-amber-900 dark:text-amber-200'}`}
                        >
                            {expirado
                                ? 'Prazo do termo vencido — assinatura perdida'
                                : pendente
                                    ? 'Biometria aguardando sua conferência'
                                    : reprovada ? 'Biometria reprovada' : 'Biometria conferida'}
                        </h4>
                        <p className={`text-[11px] leading-relaxed mt-0.5 max-w-3xl ${expirado
                            ? 'text-red-800/90 dark:text-red-200/80'
                            : 'text-amber-800/90 dark:text-amber-200/70'}`}
                        >
                            {expirado
                                ? `O cliente assinou, mas a conferência da biometria ficou parada e o prazo de assinatura venceu em ${fmtDataHora(status?.prazo_assinatura).split(',')[0]}. O Autentique cancelou o documento: a assinatura não vale mais e nenhuma decisão é aceita. O caminho é reemitir o termo e pedir que ele assine de novo.`
                                : pendente
                                    ? 'A validação automática não encontrou a face do cliente nas bases oficiais e o documento ficou bloqueado. Compare a selfie com a documentação: o PDF assinado só é gerado depois da sua decisão.'
                                    : `Decidida em ${fmtDataHora(assinatura.verificacao.verificada_em || assinatura.biometria_aprovada_em || assinatura.biometria_reprovada_em)}.`}
                        </p>
                    </div>
                    <a
                        href={status?.url_documento}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`hidden sm:inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-bold flex-shrink-0 transition-colors ${expirado
                            ? 'text-red-800 hover:bg-red-200/60 dark:text-red-300 dark:hover:bg-red-500/15'
                            : 'text-amber-800 hover:bg-amber-200/60 dark:text-amber-300 dark:hover:bg-amber-500/15'}`}
                    >
                        Ver no Autentique <ExternalLink size={11} />
                    </a>
                </div>

                <div className="flex flex-col sm:flex-row gap-5 p-5">
                    {/* Selfie */}
                    <div className="flex-shrink-0 mx-auto sm:mx-0">
                        {assinatura.selfie ? (
                            <button
                                onClick={() => setAmpliada(true)}
                                className="group relative block w-[150px] rounded-xl overflow-hidden border border-gray-300 dark:border-slate-600 hover:border-primary transition-colors bg-gray-100 dark:bg-slate-800"
                                title="Ampliar a selfie"
                            >
                                {/* Sem recorte: é uma foto de rosto para conferência, e cortar
                                    topo/laterais pode esconder justamente o que identifica. */}
                                <img
                                    src={assinatura.selfie}
                                    alt="Selfie enviada na biometria"
                                    referrerPolicy="no-referrer"
                                    className="w-full h-auto max-h-[300px] object-contain"
                                />
                                <span className="absolute inset-x-0 bottom-0 flex items-center justify-center gap-1 py-1 text-[10px] font-bold text-white bg-black/55 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <Maximize2 size={10} /> Ampliar
                                </span>
                            </button>
                        ) : (
                            <div className="w-[150px] h-[190px] rounded-xl border border-dashed border-gray-300 dark:border-slate-600 flex flex-col items-center justify-center gap-2 text-gray-400 dark:text-slate-500">
                                <ImageOff size={22} />
                                <span className="text-[10px] font-medium">Sem selfie</span>
                            </div>
                        )}
                    </div>

                    {/* Dados + falhas + ações */}
                    <div className="flex-1 min-w-0 space-y-4">
                        <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-5 gap-y-3">
                            <Campo label="Nome informado" valor={dados?.name || assinatura.nome} />
                            <Campo label="CPF" valor={fmtCpf(dados?.cpf)} mono />
                            <Campo label="Nascimento" valor={fmtNascimento(dados?.birthday)} />
                            <Campo label="Assinou em" valor={fmtDataHora(assinatura.assinado_em)} />
                            <div className="col-span-2">
                                <Campo label="E-mail no documento" valor={assinatura.email} />
                            </div>
                        </div>

                        {falhas.length > 0 ? (
                            <div className="rounded-xl border border-amber-200 bg-white/70 dark:border-slate-700/60 dark:bg-black/20 px-4 py-3">
                                <span className="block text-[9px] font-bold uppercase tracking-[0.14em] text-gray-500 dark:text-slate-400 mb-1.5">
                                    Por que a validação automática falhou
                                </span>
                                <ul className="space-y-1">
                                    {falhas.map((f, i) => (
                                        <li key={i} className="flex items-baseline gap-2 text-[11px]">
                                            <span className="font-mono font-bold text-amber-700 dark:text-amber-300 flex-shrink-0">{f.codigo}</span>
                                            <span className="text-gray-700 dark:text-slate-300">{f.mensagem}</span>
                                            {f.vezes > 1 ? (
                                                <span className="text-gray-400 dark:text-slate-500 flex-shrink-0">×{f.vezes}</span>
                                            ) : null}
                                        </li>
                                    ))}
                                </ul>
                                <p className="text-[10px] text-gray-500 dark:text-slate-400 mt-2 leading-relaxed">
                                    Falha da base biométrica, não do cliente — é comum em quem nunca tirou CNH. A conferência
                                    passa a ser sua.
                                </p>
                            </div>
                        ) : null}

                        {/* Ações */}
                        {expirado ? (
                            <div className="flex items-start gap-2 rounded-xl border border-red-200 bg-white/70 dark:border-red-500/25 dark:bg-black/20 px-4 py-3">
                                <CalendarX size={16} className="mt-0.5 flex-shrink-0 text-red-600 dark:text-red-300" />
                                <div className="text-[11px] leading-relaxed text-gray-700 dark:text-slate-300">
                                    <p className="font-bold text-red-800 dark:text-red-200 text-xs mb-0.5">Não há mais o que decidir aqui</p>
                                    Gere um novo Termo de Adesão para o cliente e acompanhe a biometria desta vez: o prazo é de
                                    10 dias a partir da emissão e continua correndo enquanto a conferência espera.
                                </div>
                            </div>
                        ) : !pendente ? (
                            <div className={`flex items-start gap-2 text-sm font-bold ${reprovada ? 'text-red-700 dark:text-red-300' : 'text-emerald-700 dark:text-emerald-300'}`}>
                                {reprovada ? <ShieldX size={16} className="mt-0.5 flex-shrink-0" /> : <CheckCircle2 size={16} className="mt-0.5 flex-shrink-0" />}
                                <span>
                                    {reprovada
                                        ? 'Assinatura invalidada — o termo precisa ser assinado novamente.'
                                        : 'Verificação concluída. O PDF assinado já pode ser baixado no Autentique.'}
                                </span>
                            </div>
                        ) : !podeDecidir ? (
                            <p className="text-[11px] text-gray-500 dark:text-slate-400">
                                Seu perfil não decide biometria. Peça a um analista, gerente ou administrador.
                            </p>
                        ) : confirmando === 'reprovar' ? (
                            <div className="space-y-2">
                                <textarea
                                    value={motivo}
                                    onChange={e => setMotivo(e.target.value)}
                                    rows={2}
                                    autoFocus
                                    placeholder="Por que a selfie não confere? (fica registrado no histórico da negociação)"
                                    className="w-full text-sm rounded-xl border border-red-300 bg-white text-gray-800 placeholder:text-gray-400 dark:border-red-500/30 dark:bg-black/30 dark:text-slate-200 dark:placeholder:text-slate-500 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-400/40 focus:border-red-400"
                                />
                                <div className="flex flex-wrap gap-2">
                                    <button
                                        onClick={() => void executar('reprovar')}
                                        disabled={motivo.trim().length < 5 || executando !== null}
                                        className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-red-600 hover:bg-red-700 text-white text-xs font-bold disabled:opacity-50 transition-colors"
                                    >
                                        {executando === 'reprovar' ? <Loader2 size={13} className="animate-spin" /> : <ShieldX size={13} />}
                                        Confirmar reprovação
                                    </button>
                                    <button
                                        onClick={() => { setConfirmando(null); setMotivo(''); }}
                                        className="px-3.5 py-2 rounded-xl border border-gray-300 text-gray-600 hover:bg-gray-100 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-800 text-xs font-bold transition-colors"
                                    >
                                        Cancelar
                                    </button>
                                </div>
                            </div>
                        ) : confirmando === 'aprovar' ? (
                            <div className="flex flex-wrap items-center gap-2">
                                <span className="text-xs font-bold text-amber-900 dark:text-amber-200">
                                    Confirma que a selfie é a mesma pessoa da documentação?
                                </span>
                                <button
                                    onClick={() => void executar('aprovar')}
                                    disabled={executando !== null}
                                    className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold disabled:opacity-50 transition-colors"
                                >
                                    {executando === 'aprovar' ? <Loader2 size={13} className="animate-spin" /> : <ShieldCheck size={13} />}
                                    Sim, aprovar
                                </button>
                                <button
                                    onClick={() => setConfirmando(null)}
                                    className="px-3.5 py-2 rounded-xl border border-gray-300 text-gray-600 hover:bg-gray-100 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-800 text-xs font-bold transition-colors"
                                >
                                    Cancelar
                                </button>
                            </div>
                        ) : (
                            <div className="space-y-2">
                                {/* O relógio do prazo não para enquanto a biometria espera —
                                    quem vê a fila precisa ver o quanto sobrou dela. */}
                                {diasRestantes !== null ? (
                                    <p className={`inline-flex items-center gap-1.5 text-[11px] font-bold ${diasRestantes <= 3
                                        ? 'text-red-700 dark:text-red-300'
                                        : 'text-amber-800 dark:text-amber-200/90'}`}
                                    >
                                        <CalendarX size={12} />
                                        {diasRestantes <= 0
                                            ? 'O prazo de assinatura vence hoje — decida agora ou a assinatura se perde.'
                                            : `Restam ${diasRestantes} dia(s) do prazo de assinatura. Depois disso o Autentique cancela o documento e a assinatura do cliente se perde.`}
                                    </p>
                                ) : null}
                                <div className="flex flex-wrap gap-2">
                                <button
                                    onClick={() => setConfirmando('aprovar')}
                                    disabled={executando !== null}
                                    className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold shadow-sm disabled:opacity-50 transition-colors"
                                >
                                    <ShieldCheck size={13} /> Aprovar biometria
                                </button>
                                <button
                                    onClick={() => void executar('liberar_tentativas')}
                                    disabled={executando !== null}
                                    className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl border border-amber-400 text-amber-800 hover:bg-amber-100 dark:border-amber-500/40 dark:text-amber-200 dark:hover:bg-amber-500/10 text-xs font-bold disabled:opacity-50 transition-colors"
                                    title="Devolve 3 tentativas ao cliente para refazer a selfie pelo link de assinatura"
                                >
                                    {executando === 'liberar_tentativas' ? <Loader2 size={13} className="animate-spin" /> : <RotateCcw size={13} />}
                                    Liberar novas tentativas
                                </button>
                                <button
                                    onClick={() => setConfirmando('reprovar')}
                                    disabled={executando !== null}
                                    className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl border border-red-300 text-red-700 hover:bg-red-50 dark:border-red-500/40 dark:text-red-300 dark:hover:bg-red-500/10 text-xs font-bold disabled:opacity-50 transition-colors"
                                >
                                    <ShieldX size={13} /> Reprovar
                                </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Selfie ampliada — a conferência de rosto precisa de tamanho, e abrir em
                outra aba tirava o analista do meio da análise. */}
            {ampliada && assinatura.selfie ? (
                <div
                    onClick={() => setAmpliada(false)}
                    className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-6 cursor-zoom-out animate-fade-in"
                >
                    <img
                        src={assinatura.selfie}
                        alt="Selfie enviada na biometria"
                        referrerPolicy="no-referrer"
                        className="max-h-[85vh] max-w-full rounded-2xl shadow-2xl"
                    />
                </div>
            ) : null}
        </>
    );
}
